/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

// helper functions

import { AreaService } from '@/services/area.service';
import * as Blockly from 'blockly/core';
import { getCurrentSatUsingRootBlock } from '@/blocks/dataset';

function getYearsArrayFromDates(fromDate, toDate) {
    let yearsArray = []
    let fromYear = fromDate.substr(0, 4);
    let toYear = toDate.substr(0, 4);
    yearsArray = [fromYear]
    let i = Number(fromYear)
    while (i < Number(toYear)) {
        yearsArray.push(`${i+1}`)
        i++
    }
    return yearsArray
}

function number_children(block, child_id) {
	if (!(typeof block.getChildren()[0] === 'undefined') && (child_id !== block.getChildren()[0].id)) {
		return 1 + number_children(block.getChildren()[0]);
	} else {
		return 0;
	}
}

function areaChange(block, inputName, fieldName) {

	if(block.getInput(inputName) == null || block.getField(fieldName) == null) {
		return false;
	}

	let prev = block.getFieldValue(fieldName);
	let newAreas = AreaService.getAreas();

	let input = block.getInput(inputName);
	input.removeField(fieldName);
	input.appendField(new Blockly.FieldDropdown(newAreas), fieldName);

	// TODO use getIndexOfValueInDropdownOptions
	let isThere = false;
	newAreas.forEach(area => {
		if(area.includes(prev)) {
			isThere = true;
		}
	});

	if(isThere) {
		block.getField(fieldName).setValue(prev);	
	} else {
		block.getField(fieldName).setValue(newAreas[0]);
	}
	
	return true;
}

function createContent(){
	return "\nglobal content" +
		"\ncontent = {" +
		"\n    'layers': []," +
		"\n    'charts': []," +
        "\n    'prompt': ''" +	
		"\n}";
}

function returnContent(legend, vis, layer) {
	return legend + "\nmapid = ee.Image(image).getMapId(" + vis + ")" +
		"\nmapURL = mapid['tile_fetcher'].url_format" +
		"\nprint('mapURL:', mapURL)" +
		"\ncontent['layers'].append({" +
		"\n    'mapURL': mapURL," +
		"\n    'mapid': mapid['mapid']," +
		"\n    'token': mapid['token']," +
		"\n    'opacity': 1," +
		"\n    'displayLayer': True," +
		"\n    'label': " + layer +
		"\n})" +
		"\ncontent['country'] = country" +
		"\ncontent['type'] = False" +
		"\ncontent['isGroup'] = False" +
		"\nif 'legend' in locals():" +
		"\n    content['legend'] = legend" +
		"\nif 'outputs' in locals():" +
		"\n    content['outputs'] = json.dumps(outputs, indent = 4)" +
		"\ncontent['layerName'] = " + layer +
		"\nbbox = boundaries.geometry().bounds().getInfo()['coordinates']" + 
		"\ncontent['bbox'] = {'SW':bbox[0][0], 'NE':bbox[0][2]}\n";
}


/*
* Helper function for generating Python code for the legend
* @param {string} type:    list/gradient
* @param {array}  palette: array of palette colors
* @param {array}  labels:  array of labels
*/

// legend title?
// no legend case: RGB
function generateLegendCode(type, palette, labels) {
	var code = "\nlegend = {}";
	code += `\nlegend['type'] = '${type}'`;
	code += "\nlegend['values'] = []";
	if (type == 'list') {
		for (let i = 0; i < palette.length; i++) {
			let str = "\nlegend['values'].append({'label': \"" + labels[i] + "\", 'colour': \"" + palette[i] + "\"})";
			code += str;
		}
	} else if (type == 'gradient') {
		for (let i = 0; i < palette.length; i++) {
			let str = "\nlegend['values'].append({'label': \"" + labels[i] + "\", 'colour': \"" + palette[i] + "\"})";
			code += str;
		}
	}

	return code;
}


function generateValueLegendCode(type, palette, labels) {
	var code = "\nlegend = {}";
	code += `\nlegend['type'] = '${type}'`;
	code += "\nlegend['values'] = []";
	if (type == 'list') {
		for (let i = 0; i < palette.length; i++) {
			let str = "\nlegend['values'].append({'label': " + labels[i] + ", 'colour': \"" + palette[i] + "\"})";
			code += str;
		}
	} else if (type == 'gradient') {
		for (let i = 0; i < palette.length; i++) {
			let str = "\nlegend['values'].append({'label': " + labels[i] + ", 'colour': \"" + palette[i] + "\"})";
			code += str;
		}
	}

	return code;
}


/*
* Helper function for stretching visualization parameters 
* based on percentile values
*/
// TO DO: visualization stretch for multiple bands
function stretchVisParams(percentile, palette = null, scale='30') {  //add scales for use in the change detection block
	let code = "\ndef AddLayerPercentStretch (img, percent):" +
		"\n    global boundaries" +
		"\n    global band" +
		"\n    lower_percentile = ee.Number(100).subtract(percent).divide(2)" +
		"\n    upper_percentile = ee.Number(100).subtract(lower_percentile)" +
		"\n    stats = img.select([band], ['value']).reduceRegion(reducer=ee.Reducer.percentile(percentiles=[lower_percentile, upper_percentile]).setOutputs(['lower', 'upper']), geometry=boundaries, scale="+scale+", bestEffort=True)" +
		"\n    vis_params = ee.Dictionary({'min': ee.Number(stats.get('value_lower')), 'max': ee.Number(stats.get('value_upper'))"
	if (palette !== null) {
		code += ", 'palette':"
		code += palette
	}
	code = code + "})" +
		"\n    return vis_params" +
		"\nvisualizParams = AddLayerPercentStretch(image, " + percentile + ").getInfo()";

	return code;
}

function multipleBandVisParamStretch(percentile) {
	var code = "\ndef AddLayerPercentStretch (img, bands, percent):" +
		"\n    global boundaries" +
		"\n    min_str = ''" +
		"\n    max_str = ''" +
		"\n    band_str = ''" +
		"\n    lower_percentile = ee.Number(100).subtract(percent).divide(2)" +
		"\n    upper_percentile = ee.Number(100).subtract(lower_percentile)" +
		"\n    for band in bands:" +
		"\n        stats = img.select([band], ['value']).reduceRegion(reducer=ee.Reducer.percentile(percentiles=[lower_percentile, upper_percentile]).setOutputs(['lower', 'upper']), geometry=boundaries, scale=30, bestEffort=True)" +
		"\n        min_str += str(ee.Number(stats.get('value_lower')).getInfo()) + \", \"" +
		"\n        max_str += str(ee.Number(stats.get('value_upper')).getInfo()) + \", \"" +
		"\n        band_str += band + \",\"" +
		"\n    vis_params = ee.Dictionary({'bands': band_str[:-1], 'min': min_str[:-2], 'max': max_str[:-2]})" +
		"\n    return vis_params" +
		"\nvisualizParams = AddLayerPercentStretch(image, image.bandNames().getInfo(), " + percentile + ").getInfo()";
	return code;
}

/*
* Helper function for generating array of export URLs for each output and polygon
*/
function urlGenerator(scale) {
	var code =
		"\nif polyg_export == True:" +
		"\n    exportArr = []" +
		"\n    file_names = []" +
		"\n    index = 1" +
		"\n    for polyg in polygons_export:" +
		"\n        download_url = image.getDownloadUrl({ 'scale': " + scale + ", 'region': polyg })" +
		"\n        exportArr.append(download_url)" +
		"\n        file_names.append('polygon_' + str(index))" +
		"\n        index += 1" +
		"\n    content['exportUrls'] = exportArr" +
		"\n    content['fileNames'] = file_names" +
		"\nelse:" +
		"\n    content['exportUrls'] = False" +
		"\n    content['fileNames'] = False";
	if (!code){/* */}
	return '';
}

function getCoordinatesForPolygon(polygon) {
	if (polygon.getBounds) {
		let bounds = polygon.getBounds();
		var NE = bounds.getNorthEast();
		var SW = bounds.getSouthWest();

		// checks for the special case that polygon crosses the antimeridian
		if(NE.lng() < SW.lng()) {
			// makes a new SW longitude that's across the antimeridian i.e < -180
			let oldSWLng = SW.lng()
			let newSWLng = -180 - (180 - oldSWLng)
			return [{
				lat: NE.lat(),
				lng: NE.lng()
			},
			{
				lat: SW.lat(),
				lng: NE.lng()
			},
			{
				lat: SW.lat(),
				lng: newSWLng
			},
			{
				lat: NE.lat(),
				lng: newSWLng
			},
			{
				lat: NE.lat(),
				lng: NE.lng()
			}]
		}

		return [{
			lat: NE.lat(),
			lng: NE.lng()
		},
		{
			lat: SW.lat(),
			lng: NE.lng()
		},
		{
			lat: SW.lat(),
			lng: SW.lng()
		},
		{
			lat: NE.lat(),
			lng: SW.lng()
		},
		{
			lat: NE.lat(),
			lng: NE.lng()
		}
		];
	} else if (polygon.getPath) {
		let polygonPath = polygon.getPath();
		let coordinates = [];
		for (let i = 0; i < polygonPath.length; i++) {
			coordinates.push({
				lat: polygonPath.getAt(i).lat(),
				lng: polygonPath.getAt(i).lng()
			});
		}

		if(doesPolygonCrossAntimeridian(coordinates)){
			coordinates.forEach(coord => {
				if(coord.lng > 0){
					coord.lng = -180 - (180 - coord.lng)
				}
			})
		}

		return coordinates;
	}

	return [];
}

function doesPolygonCrossAntimeridian(coordinates) {
	for(let i = 1; i < coordinates.length; i++){
		if(Math.abs(coordinates[i].lng - coordinates[i-1].lng) > 180.0){
			return true;
		}
	}
	return false;
}

// forces dynamicDropdown to update asap rather than waiting for the user to click on it
function refreshDynamicDropdownField(block, fieldName) {
    const field = block.getField(fieldName)
    if (field) {
        field.getOptions(false)
        // work around for https://github.com/google/blockly/issues/3553
        field.doValueUpdate_(field.getValue())
        field.forceRerender()
    }
}

/**
 * Takes the sat property and stores it in the blocks oldSat property
 * Gets the current sat and stores it in the blocks sat property
 * @param {Object} block 
 */
 function updateBlockSat(block) {
	if(block.sat === undefined) {
		block.sat = null;
	}
	block.oldSat = block.sat;

	block.sat = getCurrentSatUsingRootBlock(block);
}

/**
 * Compares the current sat to the old sat and returns true if they are the not the same
 * Use in conjunction with updateBlockSat
 * @example
 * updateBlockSat(block)
 * if(block.hasSatChanged()) {
 * 	// do something
 * }
 * @param {Object} block
 * @returns {Boolean} true when satellite from root block has changed
 */
function hasSatChanged(block) {
	if(block.oldSat !== block.sat) {
		return true;
	}
	return false;
}

/**
 * Helpful for when you've removed and added a dropdown field to change it's options and
 * you want to check if the users old selection is in the new options to remember it
 * Usage:
 * let index = getIndexOfValueInDropdownOptions(oldBandValue, bandOptions);
 * if(index !== -1) {
 *    this.setFieldValue(oldBandValue, fieldName);
 * } else {
 *     this.setFieldValue(defaultValue, fieldName);
 * }
 * @param {String} value - Machine readable value to check for within dropdown options i.e. only second option of the dropdown
 * @param {Array} dropdownOptions - Blockly field dropdown options, array of tuples e.g. [["Red", "r"], ["Green", "g"]]
 * @returns {Number} index of value in dropdown, or -1 if not found
 */
function getIndexOfValueInDropdownOptions(value, dropdownOptions) {
	let dropdownValues = dropdownOptions.map(option => option[1])
	return dropdownValues.indexOf(value);
}

export { 
	areaChange,
	multipleBandVisParamStretch,
	number_children,
	createContent,
	returnContent,
	generateLegendCode,
	generateValueLegendCode,
	stretchVisParams,
	urlGenerator,
	refreshDynamicDropdownField,
	getCoordinatesForPolygon,
	hasSatChanged,
	getIndexOfValueInDropdownOptions,
	updateBlockSat,
    getYearsArrayFromDates
};
